/**
 * PlacementPreview Component
 *
 * 行動版放置模式的半透明預覽 TimeBox。
 * 顯示在日曆上用戶選擇的時段位置，表示即將建立的 TimeBox。
 */
import React from 'react';
import { Task } from '../../../types/task';
import { format } from 'date-fns';

export interface PlacementPreviewProps {
  /** 被選取的任務 */
  task: Task;

  /** 預覽的開始時間 */
  startTime: Date;

  /** 預覽的結束時間 */
  endTime: Date;

  /** 預覽方塊的位置和尺寸（由父元件計算） */
  position: {
    top: number;    // 距離日曆頂部的像素值
    left: number;   // 距離日曆左側（像素或百分比）
    width: number;  // 預覽方塊寬度（像素或百分比）
    height: number; // 預覽方塊高度（像素）
    /** 是否使用百分比定位 left/width（預設 false 為像素） */
    usePercent?: boolean;
    /** 左右邊距（像素） */
    marginPx?: number;
  };

  /** 點擊預覽方塊時觸發（等同確認） */
  onClick?: () => void;
}

const PlacementPreview: React.FC<PlacementPreviewProps> = ({
  task,
  startTime,
  endTime,
  position,
  onClick,
}) => {
  const usePercent = position.usePercent ?? false;
  const marginPx = position.marginPx ?? 0;

  return (
    <div
      className="absolute z-placement-preview bg-sky-50 border border-dashed border-sky-400 rounded-md cursor-pointer transition-all hover:bg-sky-100"
      style={{
        top: `${position.top}px`,
        left: usePercent ? `${position.left}%` : `${position.left}px`,
        width: usePercent
          ? `calc(${position.width}% - ${marginPx * 2}px)`
          : `${position.width}px`,
        height: `${position.height}px`,
        marginLeft: marginPx ? `${marginPx}px` : undefined,
        marginRight: marginPx ? `${marginPx}px` : undefined,
      }}
      onClick={onClick}
      role="button"
      tabIndex={0}
      aria-label={`Preview TimeBox for ${task.title} from ${format(startTime, 'HH:mm')} to ${format(endTime, 'HH:mm')}`}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick?.();
        }
      }}
    >
      <div className="p-2 text-xs font-medium text-sky-700 truncate">
        {task.title}
      </div>
      <div className="px-2 text-xs text-sky-500">
        {format(startTime, 'HH:mm')} - {format(endTime, 'HH:mm')}
      </div>
    </div>
  );
};

export default PlacementPreview;
